import { useCallback, useRef, useState } from "react";
import type { Uuid } from "spacetimedb";

import { ErrorHandler } from "@/web/lib/errors";
import { useFormNavigation } from "@/web/hooks/useFormNavigation";
import { useProcedureMutation, useReducerMutation } from "@/web/lib/tanstack";
import { procedures, reducers } from "@tycho/web-bindings";
import type { SwipeDirection } from "@/web/components/primitives/swipable";
import { MAX_TOP_THREE_SELECTIONS } from "./constants";
import { formatAddressLine, resolvePriceEstimate, toUserPriceJudgement } from "./formatters";
import type {
  CompsReviewStage,
  CompsReviewSubmitPayload,
  PriceEstimateFeedback,
  ReviewComp,
  ReviewCompsActionProps,
} from "./types";

type PendingExit = {
  compId: Uuid;
  direction: SwipeDirection;
  compIndex: number;
};

export function useCompsReviewFlow({
  underwritableEntity,
  property,
  onSubmitSuccess,
}: ReviewCompsActionProps & { onSubmitSuccess: (result: CompsReviewSubmitPayload) => void }) {
  const { stage } = useFormNavigation<CompsReviewStage>("intro");
  const targetAddress = formatAddressLine(property.address);

  // Freeze the comp list when swipe review starts. Right swipes call
  // `approveComparableProperty`, which updates the super-view subscription and
  // can rebuild `property.comps` (unstable ordering). Iterating a live index
  // into that array breaks consecutive right swipes; left swipes only hid the
  // symptom because they never triggered a subscription write.
  const [reviewComps, setReviewComps] = useState<ReviewComp[] | null>(null);
  const reviewCompsRef = useRef<ReviewComp[] | null>(null);
  reviewCompsRef.current = reviewComps;

  const [currentCompIndex, setCurrentCompIndex] = useState(0);
  const [interestedCompIds, setInterestedCompIds] = useState<Uuid[]>([]);
  const [topThreeCompIds, setTopThreeCompIds] = useState<Uuid[]>([]);
  const [priceEstimateFeedback, setPriceEstimateFeedback] = useState<PriceEstimateFeedback | null>(
    null,
  );
  // The just-committed comp that's still playing its exit animation. While set,
  // the wrapper fades the card in place. Index advancement is driven by
  // Swipable's `onSwipeComplete` (after finishExit), not the wrapper fade —
  // tying advancement only to `onAnimationComplete` was unreliable once the
  // async approve reducer ran.
  const [exiting, setExiting] = useState<{ compId: Uuid; direction: SwipeDirection } | null>(null);
  const pendingExitRef = useRef<PendingExit | null>(null);
  const swipeInFlightRef = useRef(false);
  const currentCompIndexRef = useRef(currentCompIndex);
  currentCompIndexRef.current = currentCompIndex;

  const resetCompsSession = () => {
    setReviewComps(null);
    setCurrentCompIndex(0);
    setInterestedCompIds([]);
    setTopThreeCompIds([]);
    setPriceEstimateFeedback(null);
    setExiting(null);
    pendingExitRef.current = null;
    swipeInFlightRef.current = false;
    stage.reset("intro");
  };

  const startSwipeReview = () => {
    setReviewComps(property.comps);
    setCurrentCompIndex(0);
    setInterestedCompIds([]);
    setTopThreeCompIds([]);
    setPriceEstimateFeedback(null);
    setExiting(null);
    pendingExitRef.current = null;
    swipeInFlightRef.current = false;
    stage.push("swipe");
  };

  const sessionComps = reviewComps ?? [];
  const currentComp = reviewComps != null ? (reviewComps[currentCompIndex] ?? null) : null;
  const remainingCount =
    reviewComps != null ? Math.max(reviewComps.length - currentCompIndex - 1, 0) : 0;
  const interestedCompIdStrings = new Set(interestedCompIds.map((id) => id.toString()));
  const topThreeCompIdStrings = new Set(topThreeCompIds.map((id) => id.toString()));
  const interestedComps = sessionComps.filter((comp) =>
    interestedCompIdStrings.has(comp.id.toString()),
  );
  const topThreeComps = interestedComps.filter((comp) =>
    topThreeCompIdStrings.has(comp.id.toString()),
  );
  const priceEstimate = resolvePriceEstimate(property, topThreeComps);

  const approveComp = useReducerMutation(reducers.approveComparableProperty, {
    onError: ErrorHandler.default,
  });
  const setTopThreeComps = useProcedureMutation(procedures.setTopThreeComps, {
    onSuccess: () => {
      const payload: CompsReviewSubmitPayload = {
        topThreeCompIds,
        estimate: priceEstimate,
        priceEstimateFeedback,
      };
      onSubmitSuccess(payload);
    },
    onError: ErrorHandler.default,
  });

  // Awaited so Swipable holds the card at apex while the reducer runs and
  // resets it on rejection. Rethrowing is what wires that up —
  // `defaultErrorHandler` (configured on the mutation) still fires the
  // toast; the throw additionally propagates to Swipable's `.catch` so the
  // card springs back instead of advancing on a failed approval.
  //
  // On success we stash the swipe in `pendingExitRef`, mark the comp as
  // `exiting` for the wrapper fade, and let Swipable finish its exit tween.
  // `handleExitComplete` runs from `onSwipeComplete` so we never advance
  // `currentCompIndex` mid-animation (the Swipable `key` is the comp id).
  const handleSwipe = async (direction: SwipeDirection) => {
    const comps = reviewCompsRef.current;
    if (comps == null || swipeInFlightRef.current) {
      throw new Error("Swipe is not available");
    }

    const compIndex = currentCompIndexRef.current;
    const comp = comps[compIndex];
    if (comp == null) {
      throw new Error("No comp to review");
    }

    swipeInFlightRef.current = true;
    const compId = comp.id;
    pendingExitRef.current = { compId, direction, compIndex };

    try {
      if (direction === "right") {
        await approveComp.mutateAsync({
          underwritableEntityId: underwritableEntity,
          targetablePropertyId: property.id,
          comparablePropertyId: compId,
        });
      }
      setExiting({ compId, direction });
    } catch (error) {
      swipeInFlightRef.current = false;
      pendingExitRef.current = null;
      ErrorHandler.default(error);
      throw error;
    }
  };

  const handleExitComplete = useCallback(() => {
    swipeInFlightRef.current = false;

    const pending = pendingExitRef.current;
    if (pending == null) {
      setExiting(null);
      return;
    }
    pendingExitRef.current = null;

    const { compId, direction, compIndex } = pending;
    const comps = reviewCompsRef.current;
    if (comps == null) {
      setExiting(null);
      return;
    }

    let nextInterestedCompIds: Uuid[] = [];

    setInterestedCompIds((prev) => {
      nextInterestedCompIds =
        direction === "right" && !prev.some((id) => id.toString() === compId.toString())
          ? [...prev, compId]
          : prev;
      return nextInterestedCompIds;
    });

    setExiting(null);

    if (compIndex >= comps.length - 1) {
      if (
        nextInterestedCompIds.length > 0 &&
        nextInterestedCompIds.length <= MAX_TOP_THREE_SELECTIONS
      ) {
        setTopThreeCompIds(nextInterestedCompIds);
      }
      stage.replace("topThree");
      return;
    }

    setCurrentCompIndex(compIndex + 1);
  }, [stage]);

  const handleToggleTopThree = (compId: Uuid) => {
    setTopThreeCompIds((ids) => {
      if (ids.some((id) => id.toString() === compId.toString()))
        return ids.filter((id) => id.toString() !== compId.toString());
      if (ids.length >= MAX_TOP_THREE_SELECTIONS) return ids;
      return [...ids, compId];
    });
  };

  const handleComplete = () => {
    setTopThreeComps
      .mutateAsync({
        underwritableEntityId: underwritableEntity,
        targetablePropertyId: property.id,
        comparablePropertyIds: topThreeCompIds,
        userThinksPriceEstimateIs: toUserPriceJudgement(priceEstimateFeedback),
      })
      .catch(ErrorHandler.default);
  };

  return {
    stage,
    comps: property.comps,
    property,
    underwritableEntity,
    propertyAddress: property.address,
    targetAddress,
    currentComp,
    remainingCount,
    interestedComps,
    topThreeComps,
    topThreeCompIds,
    priceEstimate,
    priceEstimateFeedback,
    exitingCompId: exiting?.compId ?? null,
    setPriceEstimateFeedback,
    resetCompsSession,
    startSwipeReview,
    handleSwipe,
    handleExitComplete,
    handleToggleTopThree,
    handleComplete,
    isSubmittingTopThree: setTopThreeComps.isPending,
  };
}

export type CompsReviewFlow = ReturnType<typeof useCompsReviewFlow>;
